import { ClientPostTypeEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useFocusEffect } from '@react-navigation/native';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text, TouchableRipple } from 'react-native-paper';

import {
  ArrowOutwardIcon,
  CalendarIcon,
  LocationIcon,
  PersonIcon,
  TagIcon,
  VitalSignsTagIcon
} from '@/assets';
import { useGetGroup } from '@/features/api/hooks/grapevine-group/useGetGroup';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import { AttachmentList } from '@/features/media/components/AttachmentList';
import { ImageCarousel } from '@/features/media/components/image/ImageCarousel';
import { useGetPostTypeFeature } from '@/features/post-editor/hooks/useGetPostFeature';
import { Address } from '@/features/posts/components/detail/Address';
import { ParticipantsOverview } from '@/features/posts/components/detail/happening/ParticipantsOverview';
import { ParticipateButton } from '@/features/posts/components/detail/happening/ParticipateButton';
import { PollView } from '@/features/posts/components/detail/poll/PollView';
import { EditedHint } from '@/features/posts/components/EditedHint';
import { PostLikeButton } from '@/features/posts/components/PostLikeButton';
import { PostTypeButton } from '@/features/posts/components/PostTypeButton';
import { RestrictedLabel } from '@/features/posts/components/RestrictedLabel';
import { UrlButton } from '@/features/posts/components/UrlButton';
import { useGetDorfHelferTypeName } from '@/features/posts/hooks/useGetDorfHelferTypeName';
import { useGetPostCategoryLabel } from '@/features/posts/hooks/useGetPostCategoryLabel';
import { PostFeedType } from '@/features/posts/types/postFeed';
import type { GenericPostDetail } from '@/features/posts/types/posts';
import { getHappeningDateString } from '@/features/posts/utils/post';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { AutoLinkText } from '@/shared/components/text/AutoLinkText';
import { useBadgeStore } from '@/shared/store/useBadgeStore';
import { usePostViewStore } from '@/shared/store/usePostViewStore';
import { hasItems } from '@/shared/utils/array';
import { getPublicName, isEmptyOrNull } from '@/shared/utils/string';
import { handleOpenGps } from '@/shared/utils/url';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    image: { width: 'auto', height: 250 },
    tag: { flexDirection: 'row', gap: 8, alignItems: 'center' },
    // Note: negative margins are needed to make it aligned with other tags as we add padding to highlight clicks/hovers
    clickableTag: {
      overflow: 'hidden',
      borderRadius: 16,
      margin: -4,
      padding: 4
    },
    tagButtonLabel: { lineHeight: 0 },
    content: {
      gap: 24,
      paddingHorizontal: 16,
      paddingVertical: 24,
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    row: {
      gap: 16,
      flexWrap: 'wrap',
      flexDirection: 'row',
      alignItems: 'center'
    },
    blocked: { fontStyle: 'italic' }
  });

export type GenericPostDetailProps = Partial<GenericPostDetail>;

export const GenericPostDetailView: FC<GenericPostDetailProps> = ({
  postId,
  groupId,
  hiddenForOtherHomeAreas,
  geoAreaIds,
  images,
  attachments,
  authorName,
  categories,
  title,
  text,
  participantCount,
  participated,
  likeCount,
  liked,
  postType,
  startTime,
  endTime,
  customAddress,
  allDayEvent,
  url,
  lastModified,
  created,
  creator,
  lastEditor,
  dorfHelferType,
  suggestionCategory,
  suggestionStatus,
  katwarnEventCodeText,
  katwarnIncidentCategory,
  organizationId,
  isBlocked,
  isCivilProtection,
  poll
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'screens.post-detail'
  });

  const { isFeatureEnabled } = useFeature();
  const isHappeningParticipationEnabled = isFeatureEnabled(
    FeaturesEnum.HappeningParticipation
  );
  const { isEmergencyOrganization } = useAppState();

  const dorfHelferTypeName = useGetDorfHelferTypeName(dorfHelferType);
  const getCategoryLabel = useGetPostCategoryLabel();
  const categoryLabel = getCategoryLabel(categories);
  const isEmergency = isEmergencyOrganization(organizationId);

  const isOrganizationPost = !isEmptyOrNull(organizationId);
  const authorDisplayName =
    authorName ??
    (isOrganizationPost
      ? getPublicName(creator?.firstName, creator?.lastName)
      : undefined);

  const localType = useMemo(() => {
    if (isEmergency) return PostFeedType.EMERGENCY;
    if (isCivilProtection) return PostFeedType.CIVIL_PROTECTION;
    return postType ?? ClientPostTypeEnum.Gossip;
  }, [isEmergency, isCivilProtection, postType]);

  const postTypeFeature = useGetPostTypeFeature(localType);

  const { data: group } = useGetGroup(groupId);

  const iconProps = { fill: theme.colors.primary, width: 24, height: 24 };

  const handleOpenAddress = useCallback(async () => {
    await handleOpenGps(customAddress?.gpsLocation);
  }, [customAddress?.gpsLocation]);

  const markPostAsRead = useBadgeStore((state) => state.markPostAsRead);
  const addPostDetailViewEvent = usePostViewStore(
    (state) => state.addPostDetailView
  );

  useFocusEffect(() => {
    markPostAsRead(postId);
    addPostDetailViewEvent(postId);
  });

  if (isBlocked)
    return (
      <Text variant="bodyLarge" style={[styles.content, styles.blocked]}>
        {t('personBlocked')}
      </Text>
    );

  return (
    <>
      {!isCivilProtection && (
        <RestrictedLabel
          hiddenForOtherHomeAreas={hiddenForOtherHomeAreas}
          geoAreaIds={geoAreaIds}
        />
      )}
      {hasItems(images) && <ImageCarousel mediaItems={images} />}
      <View style={styles.content}>
        {title && <Text variant="titleLarge">{title}</Text>}

        {startTime && (
          <View style={styles.tag}>
            <CalendarIcon {...iconProps} />
            <Text variant="labelLarge" numberOfLines={2}>
              {getHappeningDateString({
                startTime,
                endTime,
                withYear: true,
                withTime: !allDayEvent
              })}
            </Text>
          </View>
        )}

        {customAddress && (
          <TouchableRipple
            onPress={() => {
              handleOpenAddress();
            }}
            style={styles.clickableTag}
          >
            <View style={styles.tag}>
              <LocationIcon {...iconProps} />
              <Address {...customAddress} />
            </View>
          </TouchableRipple>
        )}

        {authorDisplayName && (
          <View style={styles.tag}>
            <PersonIcon {...iconProps} />
            <Text variant="labelLarge">{authorDisplayName}</Text>
          </View>
        )}

        {!isCivilProtection && categoryLabel && (
          <View style={styles.tag}>
            <TagIcon {...iconProps} />
            <Text variant="labelLarge">{categoryLabel}</Text>
          </View>
        )}

        {postType === ClientPostTypeEnum.SpecialPost && dorfHelferType && (
          <View style={styles.tag}>
            <TagIcon {...iconProps} />
            <Text variant="labelLarge">{dorfHelferTypeName}</Text>
          </View>
        )}

        {(postType === ClientPostTypeEnum.Seeking ||
          postType === ClientPostTypeEnum.Offer) && (
          <View style={styles.tag}>
            <TagIcon {...iconProps} />
            <Text variant="labelLarge">{postTypeFeature.channelName}</Text>
          </View>
        )}

        {postType === ClientPostTypeEnum.Suggestion && (
          <>
            {!isEmptyOrNull(suggestionCategory?.displayName) && (
              <View style={styles.tag}>
                <TagIcon {...iconProps} />
                <Text variant="labelLarge">
                  {suggestionCategory?.displayName}
                </Text>
              </View>
            )}
            {suggestionStatus && (
              <View style={styles.tag}>
                <VitalSignsTagIcon {...iconProps} />
                <Text variant="labelLarge">
                  {t(`suggestionStatus.${suggestionStatus}`)}
                </Text>
              </View>
            )}
          </>
        )}

        {isCivilProtection && (
          <>
            {katwarnIncidentCategory && (
              <View style={styles.tag}>
                <TagIcon {...iconProps} />
                <Text variant="labelLarge">{katwarnIncidentCategory}</Text>
              </View>
            )}
            {katwarnEventCodeText && (
              <View style={styles.tag}>
                <VitalSignsTagIcon {...iconProps} />
                <Text variant="labelLarge">{katwarnEventCodeText}</Text>
              </View>
            )}
          </>
        )}

        <AutoLinkText variant="bodyLarge" text={text} />

        {postId && poll && <PollView postId={postId} poll={poll} />}

        {hasItems(attachments) && (
          <AttachmentList
            style={{ padding: 0 }}
            attachments={attachments.map((documentItem) => ({
              documentItem: documentItem,
              documentItemId: documentItem.id
            }))}
            actionIcon={ArrowOutwardIcon}
          />
        )}

        {lastModified && created && lastModified > created && (
          <EditedHint
            created={created}
            lastModified={lastModified}
            lastEditor={lastEditor}
            t={t}
          />
        )}

        {(url || postType === ClientPostTypeEnum.Happening) && (
          <View style={styles.row}>
            {url && <UrlButton url={url}>{t('learnMore')}</UrlButton>}
            {postType === ClientPostTypeEnum.Happening &&
              isHappeningParticipationEnabled && (
                <ParticipateButton
                  postId={postId}
                  participated={participated}
                />
              )}
          </View>
        )}

        {postType === ClientPostTypeEnum.Happening &&
          isHappeningParticipationEnabled && (
            <ParticipantsOverview
              postId={postId}
              style={styles.row}
              participantCount={participantCount}
            />
          )}

        <View style={[styles.row, { justifyContent: 'space-between' }]}>
          <PostLikeButton postId={postId} liked={liked} likeCount={likeCount} />
          {(postType || group) && (
            <PostTypeButton postType={localType} group={group} />
          )}
        </View>
      </View>
    </>
  );
};
